import PropTypes from 'prop-types';
import {
    useFloating,
    useInteractions,
    useRole,
    useDismiss,
    FloatingPortal,
    FloatingOverlay,
    FloatingFocusManager,
} from '@floating-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'Utils/cn';
import useFloatingProps from 'Hooks/useFloatingProps';
import OutlineClose from 'Components/Atoms/OutlineIcons/OutlineClose';
import OutlineArrowLeft from 'Components/Atoms/OutlineIcons/OutlineArrowLeft';
import IconButton from 'Components/Atoms/IconButton/IconButton';

const Overlay = motion(FloatingOverlay);

const sizeClasses = {
    xs: 'max-w-md',
    sm: 'max-w-lg',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl',
};

export default function Dialog(props) {
    const {
        open,
        onClose,
        children,
        className,
        blockClose = false,
        size = 'md',
        disableClose = false,
        title,
        zIndex,
        onBack,
        backLabel,
    } = useFloatingProps(props);

    const { refs, context } = useFloating({
        open,
        onOpenChange: (open) => {
            if (blockClose) return;

            if (!open) onClose?.();
        },
    });

    const { getFloatingProps } = useInteractions([
        useRole(context, { role: 'dialog' }),
        useDismiss(context, { enabled: !blockClose }),
    ]);

    return (
        <FloatingPortal>
            <AnimatePresence>
                {open && (
                    <Overlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                        lockScroll
                        className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-modal-backdrop flex p-3"
                        style={zIndex ? { zIndex } : undefined}
                    >
                        <FloatingFocusManager context={context}>
                            <motion.div
                                initial={{ opacity: 0, y: -28 }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                    transition: { delay: 0.2, duration: 0.15 },
                                }}
                                exit={{ opacity: 0, y: -28 }}
                                {...getFloatingProps({
                                    ref: refs.setFloating,
                                    className: cn(
                                        'relative m-auto w-full bg-white rounded-lg md:rounded-[20px] p-4 md:px-8 md:py-6 w-full shadow-xl overflow-auto',
                                        sizeClasses[size],
                                        className,
                                    ),
                                })}
                            >
                                {onClose && !disableClose && (
                                    <IconButton
                                        variant="ghost"
                                        icon={<OutlineClose />}
                                        className="absolute top-2 right-2"
                                        onClick={onClose}
                                    />
                                )}

                                {onBack ? (
                                    <div className="flex items-center gap-2 mb-4 pr-8">
                                        <IconButton
                                            variant="ghost"
                                            icon={<OutlineArrowLeft />}
                                            className="shrink-0 -ml-2"
                                            aria-label={backLabel}
                                            onClick={onBack}
                                        />
                                        <div className="text-[22px] font-semibold leading-tight">{title}</div>
                                    </div>
                                ) : (
                                    title && (
                                        <div className="text-[22px] font-semibold leading-tight mb-4 pr-8">{title}</div>
                                    )
                                )}

                                {children}
                            </motion.div>
                        </FloatingFocusManager>
                    </Overlay>
                )}
            </AnimatePresence>
        </FloatingPortal>
    );
}

Dialog.propTypes = {
    open: PropTypes.bool,
    children: PropTypes.node,
    onClose: PropTypes.func,
    className: PropTypes.string,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    disableClose: PropTypes.bool,
    blockClose: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Shows a back button before the title, as the modals stacked by ModalStackProvider.
    onBack: PropTypes.func,
    backLabel: PropTypes.string,
};
